<template>
    <!-- min-h-[60px] is a defensive floor for embedded contexts (ERA iframe, etc.) where
         the `h-full` height chain may resolve to 0 because no ancestor has an explicit
         height. Without it, items-center centers the branch content at y=0 of a zero-height
         container, and the surrounding overflow:hidden in FusionToolbar.left-column clips
         the navbar entirely. 60px matches the natural content height of the InternalMessaging
         tab row (pt-2 wrapper + 52px pill); shorter branches still render fine because
         items-center vertically centers them within the 60px space. -->
    <div id="fusion_navbar_container" class="flex items-center h-full min-h-[60px]">
      <div v-if="displayType === 'Spark'" class="self-stretch px-4 pt-2 w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <!-- Three columns, the outer two forced equal (1fr each), so the title sits on the bar's true
             center however wide New topic and the hamburger are. The side cells render even when a
             host hides their buttons, and the title is pinned to col-start-2 on top of that, so an
             empty side can never let it slide over. No min-w-0 on the side cells: when the bar is too
             narrow for both, a side column growing past its share nudges the title off-center, which
             is better than the button being allowed to shrink under it and overlap. -->
        <!-- Shown wherever FusionWeb owns which conversation is open (show_conversation_controls):
             standalone, the toolbar embedded by ERA or any other host app, and the WingCommander
             extension's Rey blade from the version that asks for it. Hidden only in a blade host that
             has not asked — an older extension re-pins its own thread id into the blade iframe URL
             and reloads the blade whenever that id changes, so a conversation switch made in here
             would be reverted by the host on its next sync. -->
        <div class="justify-self-start flex items-center">
          <!-- "+" — a NEW, UNSAVED conversation. Purely client-side: nothing is written until the
               user actually asks something (see MessengerWindow.startDraftConversation), so
               opening one and backing out leaves no empty row in the list. This replaced a control
               that used to DELETE every message on the single shared thread; with more than one
               conversation available, destroying the current one is no longer what "new" means.

               Carries its label visibly instead of hiding it in a tooltip: starting a second
               topic is the one action the conversation list exists to enable, and a bare "+"
               next to a hamburger gave no hint that Rey keeps more than one thread at all.
               A neutral secondary button with only the "+" in Rey purple. A solid purple fill
               was tried and pulled the eye off the Rey Assistant title beside it, so the purple
               is kept to the glyph and the title stays the one strong colour on the bar. Fill
               and type are the secondary-button tokens (the same grey as the Cancel buttons),
               which already swap per theme. The colours are set in CSS rather than by utilities
               so the glyph's purple can swap for dark mode through one variable. The "+" stays the
               ri-add-line glyph rather than a character in the label so the translators never
               receive a string with punctuation they would have to preserve. :title is gone:
               a tooltip that repeats text already on screen is noise, and aria-label is kept
               only because it now matches that visible text exactly. -->
          <button v-if="show_conversation_controls" type="button" :aria-label="$t('navbar.newConversation')"
                  class="rey-new-topic flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 cursor-pointer"
                  @click.prevent="startNewSparkConversation">
            <i class="ri-add-line text-base" />
            <span class="text-[12px] font-medium leading-none">{{ $t('navbar.newConversation') }}</span>
          </button>
        </div>
        <div class="col-start-2 inline-flex justify-start items-center gap-6 min-w-0">
          <div class="relative pt-3 pb-4 px-2">
            <!-- :key on the active locale forces Vue to swap in a NEW element on a language
                 switch rather than patching this one's text in place. Required because of the
                 background-clip:text fill below: the visible glyphs are a mask painted over the
                 gradient layer, and Chrome fails to invalidate that paint when the text changes
                 in place — leaving the previous language's glyphs on screen even though the DOM
                 holds the new string. Two things make it certain here: all three translations are
                 exactly 13 characters ("Rey Assistant" / "Asistente Rey" / "Assistant Rey"), so
                 the box never resizes and nothing triggers a layout-driven repaint; and the
                 element sits inside .left-column, which FusionToolbar promotes to its own
                 compositing layer with transform:translateZ(0). A fresh node has no stale paint
                 to reuse. Cost is one re-mount per language switch, which also restarts the
                 shimmer sweep — invisible at that moment.

                 The key covers the VIEW as well as the language, because the title now names the
                 surface underneath it and swaps to "Topics" when the hamburger puts the list up.
                 That swap is a milder case than a language switch — "Topics" is 6 characters
                 against "Rey Assistant"'s 13, so the box does resize and the reflow will probably
                 force the repaint by itself — but "probably" is not "always" for a text mask on a
                 promoted layer, and keying on both inputs to the painted glyphs settles it for one
                 re-mount.

                 navbar.topics is spelled out in its own $t() call rather than folded with the
                 other into a single $t(someComputedKey): i18n/index.spec.js finds a catalog's live
                 keys by scanning components for literal $t('...') calls, so a key assembled in JS
                 reads to it as an orphan and fails the no-unused-keys check. -->
            <div :key="active_locale + '|' + spark_view" class="fusion-tab-label rey-assistant-title text-center truncate" :class="{ 'rey-assistant-title--shimmer': sparkSplashActive }">{{ spark_conversations_open ? $t('navbar.topics') : $t('navbar.reyAssistant') }}</div>
            <div class="absolute left-2 right-2 bottom-0 h-1 rounded-full bg-[#6B2DF0]"></div>
          </div>
          <div v-if="false" class="relative pt-3 pb-4 px-2 cursor-pointer">
            <div class="fusion-tab-label text-center text-base leading-6 truncate text-text-default/80 font-normal">{{ $t('navbar.learningCenter') }}</div>
            <div class="absolute left-2 right-2 bottom-0 h-1 rounded-full bg-transparent"></div>
          </div>
        </div>
        <!-- Same gate as New topic; see the note above that cell. -->
        <div class="justify-self-end flex items-center">
          <!-- Hamburger — swaps the chat window for the conversation list, and back. The three bars
               are real elements rather than the ri-menu-line glyph this used to hold, because they
               morph into an X while the list is up and an icon font can only be swapped wholesale.
               They paint with currentColor, so they follow the button's own text color and need no
               color rule of their own. The shape carries the open state by itself: the button stays
               the same grey with the same hover-only background in both states — matching the "+"
               beside it — rather than sitting permanently lit while the list is up. aria-expanded
               is what reports that state to assistive tech. -->
          <button v-if="show_conversation_controls" type="button" :aria-label="$t('navbar.viewConversations')" :title="$t('navbar.viewConversations')"
                  :aria-expanded="spark_conversations_open ? 'true' : 'false'"
                  class="flex h-9 w-9 items-center justify-center rounded-xl text-icon-default hover:bg-button-ghost-hover cursor-pointer"
                  @click.prevent="toggleSparkConversations">
            <span class="rey-burger" :class="{ 'rey-burger--open': spark_conversations_open }" aria-hidden="true">
              <span class="rey-burger__bar rey-burger__bar--top"></span>
              <span class="rey-burger__bar rey-burger__bar--middle"></span>
              <span class="rey-burger__bar rey-burger__bar--bottom"></span>
            </span>
          </button>
          <!-- ⋯ — opens the OptionsMenu (profile, Schedules, General Settings, Rey preferences).
               Separate deployment opt-in (FUSION_OPTIONS_MENU_IN_HOST), parallel to the
               conversation controls' flag: embedded hosts that never send the SHOW-OPTIONS window
               message have no other way to reach the menu on this entry — the built-in trigger
               lives on the toolbar entry's left column. Glyph and colours are the FusionToolbar ⋯
               trigger's; sizing follows the hamburger so both sit alike. -->
          <!-- OptionsMenu.handleClickOutside exempts .options-menu-trigger from its close-on-
               outside-click; without that marker class the opening click bubbles to the document
               and closes the menu in the same click. -->
          <button v-if="show_options_trigger" type="button" :aria-label="$t('navbar.options')" :title="$t('navbar.options')"
                  class="options-menu-trigger flex h-9 w-9 items-center justify-center rounded-xl text-icon-default hover:bg-button-ghost-hover cursor-pointer"
                  @click.prevent="openOptionsMenu">
            <svg class="text-icon-default" style="width:1.25rem;height:1.25rem" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
            </svg>
          </button>
        </div>
      </div>
      <div v-else-if="displayType === 'InternalMessaging' && !Boolean(header_text)" class="self-stretch px-4 pt-2 w-full flex justify-between items-center gap-2">
        <template v-if="!messenger_search_active">
          <div class="inline-flex justify-start items-center gap-6">
            <div class="relative pt-3 pb-4 px-2 cursor-pointer"
                 @click.prevent="setMessagingState(null)">
              <div class="fusion-tab-label text-center text-base leading-6 truncate"
                   :class="is_contacts_view ? 'text-text-subtle font-normal hover:text-brand-outline' : 'text-brand-outline font-semibold'">
                {{ $t('common.internalMessaging') }}
              </div>
              <div class="absolute left-2 right-2 bottom-0 h-1 rounded-full" :class="is_contacts_view ? 'bg-transparent' : 'bg-brand-outline'"></div>
            </div>
            <div v-if="has_chat_access" class="relative pt-3 pb-4 px-2 cursor-pointer"
                 @click.prevent="setMessagingState('Contacts')">
              <div class="fusion-tab-label text-center text-base leading-6 truncate"
                   :class="is_contacts_view ? 'text-brand-outline font-semibold' : 'text-text-subtle font-normal hover:text-brand-outline'">
                {{ $t('common.contacts') }}
              </div>
              <div class="absolute left-2 right-2 bottom-0 h-1 rounded-full" :class="is_contacts_view ? 'bg-brand-outline' : 'bg-transparent'"></div>
            </div>
          </div>
          <button type="button" :aria-label="search_button_aria_label"
                  class="flex h-9 w-9 items-center justify-center rounded-xl text-icon-default hover:bg-button-ghost-hover cursor-pointer"
                  @click.prevent="openMessengerSearch">
            <i class="ri-search-line text-lg" />
          </button>
        </template>
        <template v-else>
          <div class="relative flex-1 min-w-0">
            <i class="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-icon-subtle text-lg pointer-events-none" />
            <input ref="messenger_search_input" type="text" v-model="messenger_search_query" autocomplete="off"
                   :placeholder="search_input_placeholder"
                   class="w-full h-10 pl-10 pr-9 rounded-xl border border-border-default bg-surface-1 text-base text-text-default placeholder:text-text-placeholder outline-none transition-[border-color,box-shadow] focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]" />
            <button v-if="messenger_search_query"
                    type="button" :aria-label="$t('common.clearSearch')"
                    class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer opacity-80 hover:opacity-100"
                    @click.prevent="messenger_search_query=''">
              <img :src="close_circle_icon" alt="" class="w-3.5 h-3.5" />
            </button>
          </div>
          <button type="button"
                  class="h-10 px-3 rounded-xl bg-btn-secondary text-text-default text-base font-semibold cursor-pointer flex-shrink-0"
                  @click.prevent="closeMessengerSearch">
            {{ $t('common.cancel') }}
          </button>
        </template>
      </div>
      
      <div v-else-if="show_back_button" class="to-nav self-stretch w-full h-14 flex justify-between items-center gap-6 relative">
        <div class="flex justify-start items-center gap-3 min-w-0">
        <div class="flex justify-start items-center gap-0.5 min-w-0 overflow-hidden">
          <div v-if="display_type.indexOf('New')===0 || display_type === 'AddToChannel'"
               class="h-10 w-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors"
               :class="is_creating_thread ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:bg-button-ghost-hover active:bg-button-ghost-active'"
               @click="!is_creating_thread && goBack()">
            <i class="ri-arrow-left-s-line text-text-placeholder ri-125" />
          </div>
          <div v-if="display_type.indexOf('Use')===0 || display_type === 'OpenContact' || display_type === 'ChatDetails'"
               class="h-10 w-10 flex items-center justify-center rounded-xl cursor-pointer flex-shrink-0 transition-colors hover:bg-button-ghost-hover active:bg-button-ghost-active"
               :title="$t('common.back')"
               @click="goBack">
            <i class="ri-arrow-left-s-line text-text-placeholder ri-125" />
          </div>
          <div class="flex justify-start items-center gap-2 min-w-0 overflow-hidden">
            <!-- Stacked avatar group: shown in DM header (UseMessage) when participants are known -->
            <div v-if="display_type === 'UseMessage' && $FUSarrayHasEntries(active_thread_participant_guids)"
                 class="flex items-center flex-shrink-0">
              <div v-for="(guid, idx) in active_thread_participant_guids.slice(0, 3)"
                   :key="`nav_avatar_${guid}`"
                   class="relative rounded-full"
                   :style="idx > 0 ? 'margin-left: -12px; box-shadow: 0 0 0 2px var(--background-page);' : ''">
                <messenger-avatar
                  :name="navbar_ent_users_by_guid[guid] ? navbar_ent_users_by_guid[guid].fullName : ''"
                  :size="32"
                  :bg-color="getNavbarAvatarColor(idx)" />
              </div>
            </div>
            <!-- Single avatar: shown for Thread/Channel/Support headers -->
            <div v-else-if="(display_type === 'UseThread' || display_type === 'UseChannel') && active_thread_nav_meta"
                 class="flex items-center flex-shrink-0">
              <messenger-avatar
                :name="active_thread_nav_meta.name"
                :size="32"
                :icon-src="nav_thread_avatar_img"
                :bg-color="nav_thread_avatar_bg_color"
                :image-zoom="nav_thread_avatar_zoom"
                :monochrome-icon="nav_thread_avatar_is_glyph" />
            </div>
            <div v-if="false" class="w-8 h-8 bg-violet-50 rounded-[100px] inline-flex flex-col justify-center items-center">
              <div class="w-10 h-10 p-2.5 bg-violet-50 rounded-[999px] inline-flex justify-center items-center gap-1.5">
                <div class="w-6 h-6 bg-violet-600"></div>
              </div>
            </div>
            <div class="inline-flex items-center gap-1 min-w-0 overflow-hidden">
              <div class="text-gray-70 text-sm font-semibold leading-snug overflow-hidden text-ellipsis whitespace-nowrap"
                   style="font-feature-settings: 'liga' off, 'clig' off;">{{ navbar_dm_header || header_text }}</div>
              <div v-if="nav_chip_icon_class"
                   class="h-9 px-2 py-1 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
                   :class="[is_private_channel ? 'cursor-pointer hover:bg-button-ghost-hover' : '', show_participants_panel ? 'bg-button-ghost-hover' : '']"
                   :title="is_private_channel ? $t('navbar.viewParticipants') : null"
                   @click.stop="is_private_channel && toggleParticipantsPanel()">
                <i :class="[nav_chip_icon_class, 'text-gray-70 text-[20px]']" />
              </div>
            </div>
          </div>
        </div>
        </div>
        <div v-if="['NewThread','NewMessage','NewChannel','AddToChannel'].includes(display_type)"
             class="h-9 px-3 rounded-xl text-sm font-medium inline-flex items-center select-none"
             :class="new_thread_create_ready && !is_creating_thread ? 'bg-btn-primary text-white cursor-pointer hover:bg-btn-primary/80' : 'bg-surface-1 text-text-default/40 cursor-not-allowed'"
             @click="triggerCreateThread">
          {{ create_button_label }}
        </div>
        <div v-else class="relative flex-shrink-0">
          <div class="h-10 w-10 flex items-center justify-center rounded-xl cursor-pointer transition-colors hover:bg-button-ghost-hover active:bg-button-ghost-active"
               :title="$t('navbar.moreOptions')"
               @click.stop="toggleMoreOptions">
            <i class="ri-more-fill ri-125" />
          </div>
          <div v-if="more_options_open && show_more_options_menu"
               class="absolute right-0 top-full mt-1 bg-surface-1 border border-border-separator rounded-xl shadow-md z-10 w-max py-1">
            <div v-if="can_view_chat_details"
                 class="flex items-center gap-2 px-3 py-2 text-sm text-text-default cursor-pointer hover:bg-button-ghost-hover rounded-lg mx-1 whitespace-nowrap"
                 @click.stop="viewChatDetails">
              {{ $t('navbar.chatDetails') }}
            </div>
            <div v-if="can_add_participants"
                 class="flex items-center gap-2 px-3 py-2 text-sm text-text-default cursor-pointer hover:bg-button-ghost-hover rounded-lg mx-1 whitespace-nowrap"
                 @click.stop="requestAddParticipants">
              {{ $t('navbar.addParticipants') }}
            </div>
            <div v-if="can_leave_thread"
                 :aria-disabled="is_leaving_thread ? 'true' : 'false'"
                 class="flex items-center gap-2 px-3 py-2 text-sm text-text-default rounded-lg mx-1 whitespace-nowrap"
                 :class="is_leaving_thread ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-button-ghost-hover'"
                 @click.stop="requestLeaveThread">
              {{ leave_thread_label }}
            </div>
            <div v-if="show_delete_separator" class="my-1 border-t border-border-separator"></div>
            <div v-if="can_delete_thread"
                 :aria-disabled="is_deleting_thread ? 'true' : 'false'"
                 class="flex items-center gap-2 px-3 py-2 text-sm text-danger rounded-lg mx-1 whitespace-nowrap"
                 :class="is_deleting_thread ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-button-ghost-hover'"
                 @click.stop="requestDeleteThread">
              {{ delete_thread_label }}
            </div>
          </div>
        </div>
        <i v-if="false" class="ri-filter-3-line ri-125 cursor-pointer" />
        <!-- Participants panel: opens from the channel (team) icon, private channels only. Anchored
             to the full-width header row and centered on its x-axis. Sits outside the overflow-hidden
             name cluster so the dropdown isn't clipped. -->
        <div v-if="show_participants_panel"
             class="absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-surface-1 border border-border-separator rounded-xl shadow-md z-10 min-w-[200px] max-h-[280px] overflow-y-auto py-2">
          <div v-for="(participant, idx) in channel_participants_display" :key="participant.id"
               class="flex items-center gap-1 h-10 px-2 py-1 rounded-md mx-1">
            <div class="flex items-center p-1 shrink-0">
              <messenger-avatar :name="participant.name" :size="24" :bg-color="getNavbarAvatarColor(idx)" />
            </div>
            <span class="text-base text-text-default truncate px-1">{{ participant.name }}</span>
          </div>
          <div v-if="!$FUSarrayHasEntries(channel_participants_display)" class="px-4 py-2 text-sm text-text-subtle">
            {{ $t('navbar.noParticipants') }}
          </div>
        </div>
      </div>
    </div>
</template>

<style scoped>
  .fusion-tab-label {
    font-family: var(--font-family-sans);
    font-feature-settings: 'liga' off, 'clig' off;
  }

  /* Rey Assistant header title — brand gradient text fill (Figma-specified: #481AD1 to #2442C9),
     hardcoded to match the design source rather than theme tokens, since it's a fixed brand mark.
     The gradient is held in --rey-title-gradient so the dark-mode override (below) can swap only
     the colors while this rule owns the text-clip + the shimmer beam layer for both themes.

     Two background layers, both clipped to the glyphs: layer 1 is a white diagonal "beam" (a 135deg
     band → a bottom-left-to-top-right streak) parked off-screen to the left at rest, layer 2 is the
     brand gradient. At rest the beam is off-screen so the title looks like a plain gradient; the
     --shimmer modifier animates the beam's position to sweep it across once, then parks it again. */
  .rey-assistant-title {
    --rey-title-gradient: linear-gradient(135deg, #481AD1 0%, #2442C9 100%);
    font-family: var(--font-family-sans);
    font-feature-settings: 'liga' off, 'clig' off;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    letter-spacing: 0.16px;
    background-image:
      linear-gradient(135deg, rgba(255, 255, 255, 0) 42%, rgba(255, 255, 255, 0.9) 50%, rgba(255, 255, 255, 0) 58%),
      var(--rey-title-gradient);
    background-size: 220% 100%, 100% 100%;
    background-position: 120% 0, 0 0;
    background-repeat: no-repeat;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .rey-assistant-title--shimmer {
    animation: rey-assistant-shimmer 15s ease-in-out infinite;
  }
  /* Sweep the beam left→right over ~1s (0%→6.67% of the 15s loop), then hold it off-screen for the
     remaining ~14s. Both endpoints (120% and -20% against the 220%-wide beam layer) sit off the
     text, so the per-loop restart is invisible. */
  @keyframes rey-assistant-shimmer {
    0%     { background-position: 120% 0, 0 0; }
    6.67%  { background-position: -20% 0, 0 0; }
    100%   { background-position: -20% 0, 0 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .rey-assistant-title--shimmer {
      animation: none;
    }
  }

  /* Hamburger → X morph on the conversation-list toggle.

     The box is 18px square — the exact em box of the text-lg ri-menu-line glyph it replaced — so
     nothing in the header shifted when the bars took over. Bars are 2px tall with centers at
     y=4/9/14, which reproduces that glyph's 5px spacing closely enough that the resting state
     reads as the same icon.

     The morph runs in two stages: the outer bars slide to the middle, THEN rotate into the X.
     Closing plays that in reverse — un-rotate, THEN slide apart — which is what makes it read as
     a rewind rather than a second, unrelated animation. That ordering needs translate and rotate
     on independent timelines, so these use the standalone `translate`/`rotate`/`scale` properties
     instead of one combined `transform`: each gets its own transition-delay, and the two rules
     below do nothing more than swap those delays. (Baseline since 2022; on anything older the
     bars hold their resting position, so the toggle is silent rather than broken.) */
  .rey-burger {
    position: relative;
    display: block;
    width: 18px;
    height: 18px;
  }
  /* The resting rule is also the CLOSING rule — a transition runs with the delays of the state it
     moves TOWARD, so these are the reverse order: rotate first (0-180ms), then the slide apart and
     the middle bar's fade back in (180-360ms). */
  .rey-burger__bar {
    position: absolute;
    left: 2px;
    right: 2px;
    height: 2px;
    border-radius: 1px;
    background: currentColor;
    transition: translate 180ms ease 180ms,
                rotate 180ms ease 0ms,
                opacity 180ms ease 180ms,
                scale 180ms ease 180ms;
  }
  .rey-burger__bar--top    { top: 3px; }
  .rey-burger__bar--middle { top: 8px; }
  .rey-burger__bar--bottom { top: 13px; }

  /* Opening: converge and drop the middle bar (0-180ms), then rotate into the X (180-360ms). */
  .rey-burger--open .rey-burger__bar {
    transition: translate 180ms ease 0ms,
                rotate 180ms ease 180ms,
                opacity 180ms ease 0ms,
                scale 180ms ease 0ms;
  }
  .rey-burger--open .rey-burger__bar--top {
    translate: 0 5px;
    rotate: 45deg;
  }
  .rey-burger--open .rey-burger__bar--bottom {
    translate: 0 -5px;
    rotate: -45deg;
  }
  /* Retracted to a point as well as faded, so the middle bar collapses into the spot the other
     two are converging on instead of ghosting in place behind them. */
  .rey-burger--open .rey-burger__bar--middle {
    opacity: 0;
    scale: 0 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .rey-burger__bar,
    .rey-burger--open .rey-burger__bar {
      transition: none;
    }
  }

  /* "New topic" — neutral secondary with a purple "+". The glyph's colour is held in
     --rey-new-topic-plus so the dark-mode override further down swaps one value, the same
     arrangement .rey-assistant-title uses above. Hover steps to the ghost-active grey rather
     than --button-secondary-focus, because in dark mode that token is the same grey as the
     resting fill and hover would show nothing. */
  .rey-new-topic {
    --rey-new-topic-plus: var(--rey-purple-60);
    background-color: var(--button-secondary-default);
    color: var(--text-button-secondary);
  }

  .rey-new-topic:hover {
    background-color: var(--button-ghost-active);
  }

  .rey-new-topic .ri-add-line {
    color: var(--rey-new-topic-plus);
  }
</style>

<style>
  /* Rey Assistant title (dark mode): the Figma-specified gradient (#481AD1/#2442C9) is
     hardcoded and doesn't adapt per theme. Against this app's dark-mode page background
     (--gray-100, #15181D) it measures ~2-2.3:1 contrast — well under WCAG AA's 4.5:1 for
     text this size, effectively unreadable. Same hue/saturation, lightened to ~70-72% HSL
     lightness so both stops clear 4.5:1 (~5.7:1 / ~6.5:1) while still reading as violet/blue. */
  [data-theme="dark"] .rey-assistant-title {
    --rey-title-gradient: linear-gradient(135deg, #9C80EF 0%, #8698E9 100%);
  }

  /* "New topic" "+" (dark mode): --rey-purple-60 is too dark to carry on the dark secondary
     grey (--gray-80, #2D3139). #9C80EF is the lighter violet the title gradient above already
     uses for the same reason. */
  [data-theme="dark"] .rey-new-topic {
    --rey-new-topic-plus: #9C80EF;
  }
</style>

<script>
  import { mapStores } from 'pinia';
  import { useMainStore } from '@store/main';
  import CloseCircleIcon from '@images/close_circle_line.svg';
  import ArrowLeftIcon from '@images/arrow-left-line.svg';
  import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';
  import SparkIcon from '@images/spark_icon.svg';
  import TacChatLogo from '@images/tac_chat_logo.svg';
  import CubeLine from '@images/cube_line.svg';
  import RoadsterLine from '@images/roadster_line.svg';

  export default {
    props: {
      displayType: {
        type: String,
        required: true,
        validator: function (value) {
          return Boolean(value && ['ContactDetails','InternalMessaging','Conversations','Spark'].indexOf(value) >= 0);
        }
      },
      // True only while the Spark welcome/splash screen is showing. Drives the one-off shimmer
      // sweep on the "Rey Assistant" title; the sweep stops once the user leaves the welcome
      // screen (sends a message or closes it), at which point FusionBody clears show_splash.
      sparkSplashActive: {
        type: Boolean,
        required: false,
        default: false,
      },
      // Whether the parent FusionBody renders an <options-menu> the ⋯ button can open. The toolbar
      // entry mounts its Spark body with :hide-options-menu="true" (it has its own ⋯ trigger in the
      // left column), so without this the button would be dead there.
      showOptionsTrigger: {
        type: Boolean,
        required: false,
        default: true,
      },
    },
    components: {
      'messenger-avatar': MessengerAvatar,
    },
    data() {
      return {
        close_circle_icon: CloseCircleIcon,
        arrow_left_icon: ArrowLeftIcon,
        spark_icon: SparkIcon,
        tac_chat_logo: TacChatLogo,
        cube_line: CubeLine,
        roadster_line: RoadsterLine,
        more_options_open: false,
        show_participants_panel: false,
      }
    },
    computed: {
      ...mapStores(useMainStore),
      // [NPM-71] Busy state for the three thread actions. Owned by the store because the
      // requests run in MessengerWindow; see the note on those state properties for why a
      // return channel over the event bus was rejected.
      is_resetting_ai_thread() {
        return this.mainStore.is_resetting_ai_thread;
      },
      spark_view: {
        get() {
          return this.mainStore.spark_view;
        },
        set(value) {
          this.mainStore.spark_view = value;
        }
      },
      // True while the hamburger has the conversation list up in place of the chat window.
      spark_conversations_open() {
        return this.spark_view === 'Conversations';
      },
      is_deleting_thread() {
        return this.mainStore.is_deleting_thread;
      },
      is_leaving_thread() {
        return this.mainStore.is_leaving_thread;
      },
      // Active catalog locale, read straight from the resolved i18n instance so a switch between
      // a base and a regional catalog of the SAME primary language (en -> en-GB) also changes the
      // key and remounts the title. Read here only to key the Rey Assistant title — see the
      // comment on that element for why it needs a re-mount rather than an in-place text patch.
      active_locale() {
        return this.$i18n ? this.$i18n.locale : this.mainStore.language;
      },
      // True when Fusion is embedded in a host application (ERA/IGNITE...). The host signals
      // itself through the wingmanConfig its extension injects via UPDATE-CONTEXT — the same
      // signal is_autovision_platform uses, generalized to any host. A standalone
      // trusted-login session has no wingmanConfig and gets the full navbar.
      is_host_embedded() {
        return Boolean(this.mainStore.wingman_config?.FusionInitType?.InitSystem);
      },
      // Whether FusionWeb owns which Rey conversation is open here — see the store's note. Set by
      // FusionMain from the entry and the host's ?rey_conversations=1.
      host_supports_rey_conversations() {
        return this.mainStore.host_supports_rey_conversations;
      },
      // Visibility-only deployment opt-in (FUSION_REY_CONVERSATIONS_IN_HOST) — shows the controls
      // without granting the thread-landing/memory behaviour of host_supports_rey_conversations.
      host_shows_conversation_controls() {
        return this.mainStore.host_shows_conversation_controls;
      },
      // Deployment opt-in (FUSION_OPTIONS_MENU_IN_HOST) — shows the ⋯ options button so an
      // embedded Spark host that never sends SHOW-OPTIONS can still open the OptionsMenu. Gated
      // by the showOptionsTrigger prop too: a FusionBody that hides its <options-menu> (the
      // toolbar entry's Spark body) would leave the button dead, so it passes false.
      show_options_trigger() {
        return this.mainStore.host_shows_options_menu && this.showOptionsTrigger;
      },
      // New topic and the conversation list. Being embedded is no longer the test on its own: the
      // toolbar is embedded by ERA and other host apps and still owns its conversations, and so does
      // an extension blade that asked for them. Only a blade host that re-pins its own thread id
      // (see host_supports_rey_conversations) is left without them.
      show_conversation_controls() {
        return !this.is_host_embedded || this.host_supports_rey_conversations || this.host_shows_conversation_controls;
      },
      header_override: {
        get() {
          return this.mainStore.header_override;
        },
        set(value) {
          this.mainStore.header_override = value;
        }
      },
      display_type() {
        return this.internal_messaging_state || this.displayType;
      },
      internal_messaging_state: {
        get() {
          return this.mainStore.internal_messaging_state;
        },
        set(value) {
          this.mainStore.internal_messaging_state = value;
        }
      },
      open_contact_guid: {
        get() {
          return this.mainStore.open_contact_guid;
        },
        set(value) {
          this.mainStore.open_contact_guid = value;
        }
      },
      contact_return_guid: {
        get() {
          return this.mainStore.contact_return_guid;
        },
        set(value) {
          this.mainStore.contact_return_guid = value;
        }
      },
      contact_origin_view: {
        get() {
          return this.mainStore.contact_origin_view;
        },
        set(value) {
          this.mainStore.contact_origin_view = value;
        }
      },
      chat_details_thread_id: {
        get() {
          return this.mainStore.chat_details_thread_id;
        },
        set(value) {
          this.mainStore.chat_details_thread_id = value;
        }
      },
      chat_details_nav_snapshot: {
        get() {
          return this.mainStore.chat_details_nav_snapshot;
        },
        set(value) {
          this.mainStore.chat_details_nav_snapshot = value;
        }
      },
      show_back_button() {
        // Defense: if a thread title (header_override) is set, we have something to render as a
        // header — force the back-button branch even if internal_messaging_state didn't get
        // synced for some reason. Without this, an inconsistent state (override truthy + state
        // null) made all three v-if branches fail and the navbar rendered blank.
        if(this.header_override) return true;
        return ['UseThread','UseMessage','UseChannel','NewThread','NewMessage','NewChannel','NewTacChat','OpenContact','AddToChannel','ChatDetails'].indexOf(this.display_type) >= 0;
      },
      is_contacts_view() {
        return this.internal_messaging_state === 'Contacts';
      },
      has_chat_access() {
        return this.mainStore.has_chat_access;
      },
      messenger_search_active: {
        get() {
          return this.mainStore.messenger_search_active;
        },
        set(value) {
          this.mainStore.messenger_search_active = value;
        }
      },
      messenger_search_query: {
        get() {
          return this.mainStore.messenger_search_query;
        },
        set(value) {
          this.mainStore.messenger_search_query = value;
        }
      },
      search_input_placeholder() {
        // 'Search conversations' (not 'Search Messages') because the underlying filter matches
        // thread/channel/team names — not message body content. Calling it 'Messages' would mislead
        // users into typing words they recall from a message and seeing "No Direct Messages Found".
        return this.is_contacts_view ? this.$t('navbar.searchContacts') : this.$t('navbar.searchConversations');
      },
      search_button_aria_label() {
        return this.is_contacts_view ? this.$t('navbar.searchContacts') : this.$t('navbar.searchConversations');
      },
      new_thread_create_ready() {
        return this.mainStore.new_thread_create_ready;
      },
      is_creating_thread() {
        return this.mainStore.is_creating_thread;
      },
      add_participants_count() {
        return this.mainStore.add_participants_count;
      },
      create_button_label() {
        return this.display_type === 'AddToChannel'
          ? this.$t('navbar.addCount', { count: this.add_participants_count })
          : this.$t('common.create');
      },
      active_thread_participant_guids: {
        get() { return this.mainStore.active_thread_participant_guids; },
        set(value) { this.mainStore.active_thread_participant_guids = value; }
      },
      active_thread_nav_meta() {
        return this.mainStore.active_thread_nav_meta;
      },
      nav_thread_avatar_img() {
        const meta = this.active_thread_nav_meta;
        if (!meta || meta.isChannel) return null;
        const t = meta.objectType;
        if (!t) return null;
        if (t.startsWith('SystemAI_')) return this.spark_icon;
        if (t === 'TACChat') return this.tac_chat_logo;
        if (t === 'Appraisal' || t === 'InventoryVehicle') return this.roadster_line;
        return this.cube_line;
      },
      // True only for the Rey spark, which is a currentColor SVG served through <img> and therefore
      // renders black. tac_chat_logo/roadster_line/cube_line carry their own colors — never tint those.
      nav_thread_avatar_is_glyph() {
        return this.nav_thread_avatar_img === this.spark_icon;
      },
      nav_thread_avatar_bg_color() {
        const meta = this.active_thread_nav_meta;
        if (!meta) return null;
        if (meta.isChannel) return meta.avatarBgColor || '#A9B3CA';
        const t = meta.objectType;
        if (t === 'Appraisal' || t === 'InventoryVehicle') return '#E3FFF1';
        return null;
      },
      nav_thread_avatar_zoom() {
        const meta = this.active_thread_nav_meta;
        if (!meta || meta.isChannel) return null;
        if (meta.objectType === 'TACChat') return 1;
        if (meta.objectType?.startsWith('SystemAI_')) return null;
        return 0.4;
      },
      nav_chip_icon_class() {
        if (this.display_type === 'UseMessage') return 'ri-chat-3-line';
        if (this.display_type === 'NewMessage') return 'ri-chat-3-line';
        if (this.display_type === 'UseChannel') return 'ri-team-line';
        if (this.display_type === 'NewChannel') return 'ri-team-line';
        if (this.display_type === 'UseThread') {
          const t = this.active_thread_nav_meta?.objectType;
          if (t === 'TACChat') return 'ri-customer-service-2-fill';
          return 'ri-hashtag';
        }
        if (this.display_type === 'NewTacChat') return 'ri-customer-service-2-fill';
        return null;
      },
      active_thread_participants() {
        return this.mainStore.active_thread_participants;
      },
      channel_participants_display() {
        // Members come from the thread's ParticipantIds, resolved to names by the join response
        // (MessengerWindow stores them on active_thread_participants). System/bot ids (e.g. the AV
        // AI chat user) are filtered out so only real members are listed.
        return (this.active_thread_participants || [])
          .filter(u => u && u.id && u.id.indexOf('system_') < 0)
          .map(u => ({ id: u.id, name: u.name || this.$t('common.unknown') }));
      },
      navbar_enterprise_users() {
        return this.mainStore.enterprise_users_data?.enterpriseUsers || [];
      },
      navbar_ent_users_by_guid() {
        return this.$FUSarrayOfObjectsKeyMap(this.navbar_enterprise_users, 'personGuid') || {};
      },
      navbar_dm_header() {
        if (this.display_type !== 'UseMessage' || !this.$FUSarrayHasEntries(this.active_thread_participant_guids))
          return null;
        // Resolve every other participant to a full display name (ghost fallback for unknown guids),
        // sorted so the "first" name matches the thread-list row, then collapse to the compact
        // "Alice", "Alice and Bob", or "Alice and 2 more" label.
        const names = this.active_thread_participant_guids
          .map(guid => {
            const user = this.navbar_ent_users_by_guid[guid];
            return user ? this.$FUSuserDisplayName(user) : this.$FUSresolveGhostUser(guid).friendlyName;
          })
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        if (!names.length) return null;
        return this.$FUSformatParticipantNames(names);
      },
      show_filter_button() {
        return ['InternalMessaging'].indexOf(this.display_type) >= 0;
      },
      show_more_options_menu() {
        if (!['UseThread', 'UseMessage', 'UseChannel'].includes(this.display_type)) return false;
        return this.can_delete_thread || this.can_leave_thread || this.can_view_chat_details || this.can_add_participants;
      },
      delete_thread_label() {
        if (this.display_type === 'UseMessage') return this.$t('navbar.deleteConversation');
        if (this.display_type === 'UseChannel') return this.$t('navbar.deleteChannel');
        if (this.is_support_thread) return this.$t('navbar.deleteSupportChat');
        return this.$t('navbar.deleteThread');
      },
      user_access_info() {
        return this.mainStore.user_access_info;
      },
      current_user_guid() {
        return this.user_access_info?.userId || null;
      },
      is_enterprise_admin() {
        // Enterprise admin (the FusionMgmt "Admin" badge / ENTERPRISEADMIN role) or Reynolds global admin.
        return Boolean(this.user_access_info?.isFusionAdmin || this.user_access_info?.isGlobalAdmin);
      },
      is_private_channel() {
        const meta = this.active_thread_nav_meta;
        return this.display_type === 'UseChannel' && Boolean(meta) && meta.isPublic === false;
      },
      is_private_thread() {
        // Object/vehicle threads (the Threads tab) are private/invite-only by nature. They get the
        // same Chat Details / Add Participants / Delete menu as private channels. Support (TACChat)
        // and SystemAI threads are excluded — they keep their own minimal menu.
        const meta = this.active_thread_nav_meta;
        if (this.display_type !== 'UseThread' || !meta) return false;
        if (this.is_support_thread) return false;
        const t = meta.objectType;
        return Boolean(t) && !t.startsWith('SystemAI_');
      },
      active_thread_is_channel() {
        // Used to branch titles / back-navigation on the shared ChatDetails + AddToChannel states,
        // which now serve both private channels and private threads.
        return Boolean(this.active_thread_nav_meta?.isChannel);
      },
      is_channel_owner() {
        const ownerId = this.active_thread_nav_meta?.createdByUserId;
        return Boolean(ownerId) && Boolean(this.current_user_guid) && ownerId === this.current_user_guid;
      },
      is_support_thread() {
        // A Support (TACChat) thread. Case-insensitive to match the server, which reconciles the
        // TACChat/TacChat casing pair. Only ever used to GRANT delete below.
        const t = this.active_thread_nav_meta?.objectType;
        return this.display_type === 'UseThread' && typeof t === 'string' && t.toLowerCase() === 'tacchat';
      },
      can_delete_thread() {
        // Every thread type except Support (TACChat): only the creator or an enterprise/global admin may
        // delete. Threads with no recorded creator (legacy docs) are admin-only.
        // Support (TACChat) threads: any participant may delete. They are created with no recorded
        // creator, so the rule above would leave the button to admins only. The server (CanUserDeleteThread)
        // re-checks membership, and the delete only hides the thread from this user's own list.
        return this.is_channel_owner || this.is_enterprise_admin || this.is_support_thread;
      },
      is_group_dm() {
        // A DM with more than two people (a group DM). Two-person DMs are excluded — leaving one
        // would strand the other party in a one-person conversation. participantCount counts the
        // non-system members (including self); it is set on the nav meta by MessengerWindow.
        if (this.display_type !== 'UseMessage') return false;
        const count = this.active_thread_nav_meta?.participantCount;
        return Number.isFinite(count) && count > 2;
      },
      can_leave_thread() {
        // A non-owner participant can leave a private channel, a private (object/vehicle) thread, or a
        // group DM. Support (TACChat) and SystemAI threads are excluded via is_private_thread. So a
        // non-owner admin sees BOTH Leave and Delete; a regular participant sees only Leave; the owner
        // sees only Delete (they delete the conversation rather than leave it).
        return (this.is_private_channel || this.is_private_thread || this.is_group_dm) && !this.is_channel_owner;
      },
      leave_thread_label() {
        // Mirrors delete_thread_label: the menu verb matches the conversation type.
        if (this.display_type === 'UseChannel') return this.$t('navbar.leaveChannel');
        if (this.display_type === 'UseThread') return this.$t('navbar.leaveThread');
        return this.$t('navbar.leaveConversation');
      },
      can_add_participants() {
        // The owner or an enterprise/global admin can invite more members to a private channel or
        // a private thread.
        return (this.is_private_channel || this.is_private_thread) && (this.is_channel_owner || this.is_enterprise_admin);
      },
      can_view_chat_details() {
        // Details is visible to all participants of a private channel or private thread, not just the owner.
        return this.is_private_channel || this.is_private_thread;
      },
      show_delete_separator() {
        // Gray divider above Delete, but only when something sits above it. On DMs/public channels
        // Delete is the lone item, so no stray line at the top of the menu.
        return this.can_delete_thread && (this.can_view_chat_details || this.can_add_participants || this.can_leave_thread);
      },
      header_text() {
        // The details page titles itself ahead of header_override, which still holds the open
        // conversation's name (the message_header watcher sets it while the thread/channel is
        // selected, and selected_thread stays set on this page). Reads "Thread Details" when opened
        // from a private thread, "Channel Details" from a channel.
        if(this.display_type === 'ChatDetails')
          return this.active_thread_is_channel ? this.$t('navbar.channelDetails') : this.$t('navbar.threadDetails');

        switch (this.display_type) {
          case 'ContactDetails':
            return this.$t('common.contact');
          case 'Conversations':
            return this.$t('common.conversations');
          case 'UseChannel':
          case 'UseThread':
          case 'UseMessage':
            // Viewing a thread: the open conversation's name (set by MessengerWindow's
            // message_header watcher) is the title. header_override is honored ONLY here so a
            // stale name can't leak onto the state-owned-title screens below — e.g. backing out
            // of a DM to the OpenContact profile, where selected_thread (and thus header_override)
            // is still set because backToMessages only clears it when returning to the list (null).
            return this.header_override || this.$t('common.internalMessaging');
          case 'NewThread':
            return this.$t('navbar.newThread');
          case 'NewMessage':
            return this.$t('navbar.newMessage');
          case 'NewChannel':
            return this.$t('navbar.newChannel');
          case 'NewTacChat':
            return this.$t('navbar.newSupportChat');
          case 'OpenContact':
            return this.$t('common.contacts');
          case 'AddToChannel':
            return this.active_thread_is_channel ? this.$t('navbar.addToChannel') : this.$t('navbar.addToThread');
          case 'ChatDetails':
            return this.active_thread_is_channel ? this.$t('navbar.channelDetails') : this.$t('navbar.threadDetails');
        }

        // Defensive fallback (mirrors show_back_button): an open conversation's name with a
        // display_type we don't explicitly handle (e.g. internal_messaging_state didn't sync).
        if(this.header_override)
          return this.header_override;

        return null;
      },
    },
    mounted() {
      window.event_bus.$on('CLOSE-OVERLAYS', this.closeMoreOptions);
      window.event_bus.$on('CLOSE-OVERLAYS', this.closeParticipantsPanel);
    },
    beforeUnmount() {
      window.event_bus.$off('CLOSE-OVERLAYS', this.closeMoreOptions);
      window.event_bus.$off('CLOSE-OVERLAYS', this.closeParticipantsPanel);
    },
    methods: {
      toggleMoreOptions() {
        this.closeParticipantsPanel();
        this.more_options_open = !this.more_options_open;
        if (this.more_options_open) {
          const self = this;
          this.$nextTick(() => {
            document.addEventListener('click', self.closeMoreOptions);
          });
        } else {
          document.removeEventListener('click', this.closeMoreOptions);
        }
      },
      closeMoreOptions() {
        this.more_options_open = false;
        document.removeEventListener('click', this.closeMoreOptions);
      },
      toggleParticipantsPanel() {
        this.closeMoreOptions();
        this.show_participants_panel = !this.show_participants_panel;
        if (this.show_participants_panel) {
          const self = this;
          this.$nextTick(() => {
            document.addEventListener('click', self.closeParticipantsPanel);
          });
        } else {
          document.removeEventListener('click', this.closeParticipantsPanel);
        }
      },
      closeParticipantsPanel() {
        this.show_participants_panel = false;
        document.removeEventListener('click', this.closeParticipantsPanel);
      },
      requestNewAiConversation() {
        // Spark/Rey header control: MessengerWindow owns the reset (API call + thread
        // reload), mirroring the MESSENGER-DELETE-THREAD ownership split.
        // [NPM-71] MessengerWindow guards re-entry too; this stops the emit at the source.
        if (this.is_resetting_ai_thread) return;
        window.event_bus.$emit('MESSENGER-RESET-AI-THREAD');
      },
      startNewSparkConversation() {
        // MessengerWindow owns the draft (it holds the message list this has to clear), same
        // ownership split as requestNewAiConversation above. Leaving the list view is done here
        // because the list is store-driven and the navbar already owns that flag.
        this.spark_view = 'Chat';
        window.event_bus.$emit('SPARK-NEW-CONVERSATION');
      },
      toggleSparkConversations() {
        this.spark_view = this.spark_conversations_open ? 'Chat' : 'Conversations';
      },
      // ⋯ button (FUSION_OPTIONS_MENU_IN_HOST): FusionBody owns show_options and the OptionsMenu
      // below it, so the click travels as an emit up the one parent→child edge that already exists.
      // The handler stamps a fresh timestamp — the same shape the SHOW-OPTIONS window message
      // gives FusionBody — which is what OptionsMenu's triggerShowOptions watcher toggles on.
      openOptionsMenu() {
        this.$emit('openOptionsMenu');
      },
      requestDeleteThread() {
        if (this.is_deleting_thread) return;   // [NPM-71]
        this.closeMoreOptions();
        window.event_bus.$emit('MESSENGER-DELETE-THREAD');
      },
      requestLeaveThread() {
        if (this.is_leaving_thread) return;   // [NPM-71]
        this.closeMoreOptions();
        window.event_bus.$emit('MESSENGER-LEAVE-THREAD');
      },
      requestAddParticipants() {
        this.closeMoreOptions();
        // Switch the open channel into the add-members picker; selected_thread stays set in
        // MessengerWindow so the invite targets this channel and Back returns to it.
        this.internal_messaging_state = 'AddToChannel';
      },
      viewChatDetails() {
        this.closeMoreOptions();
        // Open the Channel Details page. selected_thread stays set in MessengerWindow so the open
        // channel's nav meta + participants remain available, and Back returns to the channel.
        this.internal_messaging_state = 'ChatDetails';
      },
      triggerCreateThread() {
        if (!this.new_thread_create_ready) return;
        window.event_bus.$emit('NAVBAR-CREATE-THREAD');
      },
      goBack() {
        // From the OpenContact detail page, go back to the Contacts list (not all the way out
        // to the Internal Messaging tab) so users return to where they were browsing.
        if(this.internal_messaging_state === 'OpenContact') {
          // Leaving the contact profile: the open thread (if any) is no longer in view.
          // Clear header_override so the stale DM name cannot bleed into the Contacts tab header.
          this.header_override = null;
          this.active_thread_participant_guids = [];
          // If this profile was opened from the Chat/Thread Details participant list, return there
          // rather than the Contacts list. Consume the breadcrumb so it can't fire on a later,
          // unrelated Back press.
          const originView = this.contact_origin_view;
          this.contact_origin_view = null;
          if(originView === 'ChatDetails') {
            // If the user drilled into a shared chat from this profile, that open overwrote the
            // details page's conversation context. Restore the details page from the snapshot taken
            // when the detour began — no network reload. The kept chat_details_thread_id defers the
            // actual thread re-open until the user backs out of the details page into the chat.
            // With no detour the context is still intact, so just show the details page directly.
            if(this.chat_details_thread_id)
              window.event_bus.$emit('MESSENGER-RESTORE-CHAT-DETAILS');
            else
              this.internal_messaging_state = 'ChatDetails';
            return;
          }
          this.internal_messaging_state = 'Contacts';
          return;
        }
        // If the New Message screen or an open chat was reached FROM a contact profile, return to
        // that profile rather than the messenger list. EnterpriseContactDetails leaves the
        // contact's guid in contact_return_guid on hand-off; consume it here so it fires once.
        // A shared chat opened from a contact profile is a DM, so it normally lands in UseMessage
        // (openThread derives the state from the thread's type). But if the thread isn't cached yet,
        // openThread falls back to the active sub-tab, which can yield UseChannel/UseThread — so all
        // of those must also return to the profile here.
        if(this.contact_return_guid && ['NewMessage','UseMessage','UseChannel','UseThread'].indexOf(this.internal_messaging_state) >= 0) {
          const returnGuid = this.contact_return_guid;
          this.contact_return_guid = null;
          // Leaving thread/new-message view: clear the stale thread title and participant guids.
          this.header_override = null;
          this.active_thread_participant_guids = [];
          this.open_contact_guid = returnGuid;
          this.internal_messaging_state = 'OpenContact';
          return;
        }
        // From the Add-participants picker, return to the open conversation rather than closing it.
        // The picker and details page are shared by channels and private threads — return to the
        // matching use-state so the back-arrow lands on the conversation it was opened from.
        if(this.internal_messaging_state === 'AddToChannel') {
          this.internal_messaging_state = this.active_thread_is_channel ? 'UseChannel' : 'UseThread';
          return;
        }
        // From the details page, return to the open conversation rather than closing it.
        if(this.internal_messaging_state === 'ChatDetails') {
          // After a shared-chat detour the live thread context still points at the shared chat, so
          // re-open the subject conversation now — the moment the user actually re-enters it — to
          // restore its messages + thread join. Consume the detour breadcrumbs. openThread derives
          // the Use* state from the active sub-tab, landing on the conversation.
          if(this.chat_details_thread_id) {
            const subjectThreadId = this.chat_details_thread_id;
            this.chat_details_thread_id = null;
            this.chat_details_nav_snapshot = null;
            window.event_bus.$emit('MESSENGER-OPEN-THREAD', subjectThreadId);
            return;
          }
          this.internal_messaging_state = this.active_thread_is_channel ? 'UseChannel' : 'UseThread';
          return;
        }
        // Any other exit clears the contact-return breadcrumb so it can't fire on a later,
        // unrelated Back press.
        this.contact_return_guid = null;
        this.active_thread_participant_guids = [];
        this.internal_messaging_state = null;
      },
      getNavbarAvatarColor(idx) {
        const colors = this.$FUSfusionAvatarColorsHex;
        return colors[idx % colors.length];
      },
      setMessagingState(value) {
        // Defensive guard: Contacts requires chat access (the only path out is to start a DM
        // via NewMessage/CreateThread). TAC-only users have no use for it.
        if(value === 'Contacts' && !this.has_chat_access) return;
        // A pill tap is always a "reset to defaults" gesture — tear down any in-flight search
        // even when tapping the currently-active pill (state doesn't change, but the user is
        // signalling they want a clean slate). Reset happens before the same-value bail-out so
        // the gesture still works on the active pill.
        this.resetMessengerSearch();
        // A pill tap is a reset gesture — drop any pending contact-return breadcrumb so Back from
        // a thread opened afterward doesn't wrongly bounce to a contact profile.
        this.contact_return_guid = null;
        // Same reset for the contact-origin breadcrumb so Back from a later contact profile doesn't
        // wrongly bounce to a stale Chat Details page, and the shared-chat-detour return thread +
        // its nav snapshot.
        this.contact_origin_view = null;
        this.chat_details_thread_id = null;
        this.chat_details_nav_snapshot = null;
        if(this.internal_messaging_state === value) return;
        this.internal_messaging_state = value;
      },
      openMessengerSearch() {
        this.messenger_search_active = true;
        const self = this;
        // Defer until the input is rendered, then focus it
        this.$nextTick(() => {
          self.$refs.messenger_search_input?.focus?.();
        });
      },
      closeMessengerSearch() {
        this.resetMessengerSearch();
      },
      resetMessengerSearch() {
        this.messenger_search_active = false;
        this.messenger_search_query = '';
      },
  }
  }
</script>
